import i18n from 'i18n/config';
import parse from 'html-react-parser';
import { format } from 'date-fns';
import { TranslateFn } from 'hooks/useTranslate';

import { Council, Society } from 'types';
import { getFileDate, getFileSize } from 'utils/files';
import { DATE_FORMAT } from 'constants/defaultConstants';
import { formatDateToTime } from 'constants/formats';
import { getSocietyEmailConfiguration } from 'utils/emails';
import { getCouncilAnnouncementCommunication } from 'modules/administratives/modals/CouncilAnnouncementModal/utils/getCouncilAnnouncementCommunication';
import { CouncilSignatureTypes } from 'types/Council';

import { getCouncilTypeLabel } from '../../CouncilDetails/utils/getCouncilTypeLabel';
import { getCouncilAttendanceTypeLabel } from '../../CouncilDetails/utils/getCouncilAttendanceTypeLabel';
import { getSignatureDescription } from './getSignatureDescription';
import {
  ContentTypes,
  DocumentsGroupItem,
} from '../hooks/useCouncilMenuOptions';

export const generateDocumentGroups = (
  council: Council,
  documents: any,
  society: Society,
  t: TranslateFn,
  isAdmin: boolean
) => {
  const {
    announcementCommunication,
    announcementDocument,
    actDocument,
    actCertificateDocument,
    actDocumentSignature,
    actCertificateDocumentSignature,
  } = documents || {};

  const societyTheme = getSocietyEmailConfiguration(society, {
    addSuffix: false,
  })?.theme;

  const hasWhiteBranding = society?.configuration?.hasWhiteBranding;

  const announcementItems: DocumentsGroupItem[] = [
    {
      name: t('Email'),
      size: null,
      date: announcementCommunication
        ? getFileDate(announcementCommunication, 'MMM dd, yyyy')
        : '',
      description: announcementCommunication ? t('EmailSent') : '',
      content: {
        name: announcementCommunication?.subject || '-',
        editorModel: parse(
          getCouncilAnnouncementCommunication(
            societyTheme,
            hasWhiteBranding,
            {
              time: council?.date ? formatDateToTime(council?.date) : '-',
              councilName: council?.name || '',
              date: council?.date
                ? format(new Date(council?.date), DATE_FORMAT)
                : '-',
              societyName: society?.name || '-',
              announcement: council?.announcementText || '',
              place: council?.place || '-',
              onlineAccess: council?.onlineAccess || '-',
              attendanceType: getCouncilAttendanceTypeLabel(council) || '-',
              councilType: getCouncilTypeLabel(council) || '-',
            },
            i18n?.language || 'es'
          )
        ),
      },
      contentType: ContentTypes.EMAIL,
      isPublic: true,
    },
    {
      name: t('CouncilAgenda'),
      size: null,
      date: getFileDate(announcementDocument, 'MMM dd, yyyy'),
      description: announcementDocument?.name,
      content: announcementDocument?.editorModel
        ? {
            name: announcementDocument.name,
            editorModel: parse(announcementDocument.editorModel || ''),
          }
        : announcementDocument,
      contentType: announcementDocument?.editorModel
        ? ContentTypes.TEMPLATE
        : ContentTypes.PDF,
      isPublic: true,
    },
  ];

  const actItems = [
    {
      name: t('Act'),
      size: null,
      date: getFileDate(actDocument, 'MMM dd, yyyy'),
      description: actDocument?.name || '-',
      content: actDocument?.editorModel
        ? {
            name: actDocument?.name || '-',
            editorModel: parse(actDocument?.editorModel || ''),
          }
        : actDocument,
      contentType: actDocument?.editorModel
        ? ContentTypes.TEMPLATE
        : ContentTypes.PDF,
      isPublic: council?.isActDocumentPublic || isAdmin || false,
    },
    {
      name: t('Certificate'),
      size: null,
      date: getFileDate(actCertificateDocument, 'MMM dd, yyyy'),
      description: actCertificateDocument?.name || '-',
      content: actCertificateDocument?.editorModel
        ? {
            name: actCertificateDocument?.name || '-',
            editorModel: parse(actCertificateDocument?.editorModel || ''),
          }
        : actCertificateDocument,
      contentType: actCertificateDocument?.editorModel
        ? ContentTypes.TEMPLATE
        : ContentTypes.PDF,
      isPublic: council?.isActCertificateDocumentPublic || isAdmin || false,
    },
  ];

  const signItems = [
    {
      name: t('Act'),
      size: getFileSize(actDocumentSignature),
      date: getFileDate(actDocumentSignature, 'MMM dd, yyyy'),
      description: getSignatureDescription(
        council,
        actDocumentSignature,
        CouncilSignatureTypes.ACT
      ),
      content: actDocumentSignature,
      contentType: ContentTypes.PDF,
      isPublic: true,
    },
    {
      name: t('Certificate'),
      size: getFileSize(actCertificateDocumentSignature),
      date: getFileDate(actCertificateDocumentSignature, 'MMM dd, yyyy'),
      description: getSignatureDescription(
        council,
        actCertificateDocumentSignature,
        CouncilSignatureTypes.CERTIFICATE
      ),
      content: actCertificateDocumentSignature,
      contentType: ContentTypes.PDF,
      isPublic: true,
    },
  ];

  return {
    announcementItems,
    actItems,
    signItems,
  };
};
